import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, Clock, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { cn } from "../lib/utils";

export default function Checkout() {
  const { items, subtotal, count, clear } = useCart();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [zones, setZones] = useState([]);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: "",
    zone_code: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/delivery/zones");
      setZones(data.zones);
    })();
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (items.length === 0) navigate("/menu");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live quote whenever zone or cart changes
  useEffect(() => {
    if (!form.zone_code || items.length === 0) {
      setQuote(null); setQuoteError(""); return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/orders/quote", {
          items: items.map((i) => ({ id: i.id, quantity: i.quantity, variant_name: i.variant_name || null })),
          zone_code: form.zone_code,
          customer_name: "quote", customer_phone: "quote", customer_email: "q@q.q", notes: "quote-only",
          promo_code: null,
        });
        if (!cancelled) { setQuote(data); setQuoteError(""); }
      } catch (err) {
        if (!cancelled) { setQuote(null); setQuoteError(err.response?.data?.detail || "Could not price this zone."); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zone_code, items]);

  const selectedZone = zones.find((z) => z.code === form.zone_code);

  const total = quote ? quote.total : subtotal;
  const canSubmit =
    form.customer_name.trim() &&
    form.customer_phone.trim() &&
    form.customer_email.trim() && form.customer_email.includes("@") &&
    form.notes.trim().length >= 10 &&
    form.zone_code &&
    quote &&
    !quoteError &&
    items.length > 0 &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ id: i.id, quantity: i.quantity, variant_name: i.variant_name || null })),
        zone_code: form.zone_code,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim(),
        notes: form.notes.trim(),
        promo_code: null,
      };
      const { data } = await api.post("/orders/place", payload);
      clear();
      navigate(`/order/${data.order_id}?token=${data.lookup_token}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place your order. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="checkout-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="mb-10">
        <span className="font-label text-xs text-[#D02030]">Checkout</span>
        <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95] tracking-tighter">
          Place your order.
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {!user && (
            <div className="chop-card p-5 flex items-center justify-between gap-4 bg-[#F4EFEB]/60">
              <div>
                <p className="font-display text-lg">Guest checkout is fine.</p>
                <p className="text-sm text-[#4E5C4F]">Or sign in with Google to save your orders.</p>
              </div>
              <Button type="button" onClick={login} variant="outline" className="rounded-full border-[#1A6B2E]/25">
                Sign in
              </Button>
            </div>
          )}

          {/* CUSTOMER DETAILS */}
          <section className="chop-card p-6 md:p-8 space-y-5">
            <div className="flex items-baseline gap-3">
              <span className="font-label text-[10px] text-[#D02030]">Step 1</span>
              <h2 className="font-display text-2xl">Your details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full name *</Label>
                <Input id="name" data-testid="input-name" value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  placeholder="Amaka Okoro" required />
              </div>
              <div>
                <Label htmlFor="phone">Phone number *</Label>
                <Input id="phone" data-testid="input-phone" value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                  placeholder="+1 226 555 1234" required />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email address *</Label>
                <Input id="email" data-testid="input-email" type="email" required
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  placeholder="you@example.com" />
              </div>
            </div>
          </section>

          {/* NOTICE + NOTES */}
          <section className="chop-card p-6 md:p-8 space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="font-label text-[10px] text-[#D02030]">Step 2</span>
              <h2 className="font-display text-2xl">Delivery details</h2>
            </div>

            <div data-testid="lead-time-notice"
                 className="rounded-xl bg-[#D02030]/8 border-l-4 border-[#D02030] p-4 flex gap-3">
              <Clock className="w-5 h-5 text-[#D02030] shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-[#0E2B13]">
                  Please allow at least 72 hours (3 full days) to fulfill your order.
                </p>
                <p className="text-[#4E5C4F] mt-1">
                  In the notes box below, tell us your <b>preferred delivery date</b> and your <b>full delivery address</b> (street, city, postal code).
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Preferred delivery date & address *</Label>
              <Textarea id="notes" data-testid="input-notes" rows={5}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={"Example:\nPreferred delivery: Saturday March 8\nAddress: 47 King St W, Hamilton, ON, L8P 1A1\nAny extras: extra pepper please 🌶️"}
                required minLength={10} />
              <p className="mt-1 text-[10px] text-[#4E5C4F]">Free-text — no calendar, we'll confirm the date by phone if anything's unclear.</p>
            </div>
          </section>

          {/* ZONE */}
          <section className="chop-card p-6 md:p-8 space-y-4">
            <div className="flex items-baseline gap-3">
              <span className="font-label text-[10px] text-[#D02030]">Step 3</span>
              <h2 className="font-display text-2xl">Delivery zone</h2>
            </div>
            <div>
              <Label>Pick your zone *</Label>
              <Select value={form.zone_code} onValueChange={(v) => setForm({ ...form, zone_code: v })}>
                <SelectTrigger data-testid="select-zone">
                  <SelectValue placeholder="Choose your delivery area" />
                </SelectTrigger>
                <SelectContent>
                  {zones.map((z) => (
                    <SelectItem key={z.code} value={z.code} data-testid={`zone-option-${z.code}`}>
                      {z.city} — {z.fee === 0 ? (z.min_subtotal ? "FREE (min $" + z.min_subtotal + ")" : "FREE") : `$${z.fee.toFixed(2)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedZone && (selectedZone.notes || selectedZone.frozen_only || selectedZone.fresh_surcharge || selectedZone.min_subtotal) && (
              <div data-testid="zone-notice" className="rounded-xl bg-[#E29A26]/12 border-l-4 border-[#E29A26] p-3 flex gap-2">
                <Info className="w-4 h-4 text-[#B57516] shrink-0 mt-0.5" />
                <div className="text-xs text-[#0E2B13] space-y-1">
                  {selectedZone.notes && <p>{selectedZone.notes}</p>}
                  {selectedZone.frozen_only && (
                    <p><b>Frozen-only zone.</b> Orders here are shipped frozen — no fresh delivery available.</p>
                  )}
                </div>
              </div>
            )}
            {quoteError && (
              <div data-testid="quote-error" className="rounded-xl bg-[#D02030]/8 border border-[#D02030]/25 p-3 flex gap-2 text-sm text-[#D02030]">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {quoteError}
              </div>
            )}
          </section>
        </div>

        {/* SUMMARY */}
        <aside className="lg:col-span-1">
          <div className="chop-card chop-shadow p-6 md:p-7 lg:sticky lg:top-24">
            <h3 className="font-display text-2xl mb-4">Order summary</h3>
            <div className="space-y-3 max-h-56 overflow-y-auto chop-scroll pr-1">
              {items.map((it) => (
                <div key={it.cartKey} className="flex items-center gap-3 text-sm" data-testid={`summary-item-${it.cartKey}`}>
                  <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#1A6B2E]/10" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{it.name}</p>
                    <p className="text-xs text-[#4E5C4F]">{it.variant_name ? `${it.variant_name} · ` : ""}× {it.quantity}</p>
                  </div>
                  <p className="font-display">${(it.price * it.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-[#1A6B2E]/10 space-y-2 text-sm">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} testid="sum-subtotal" />
              <Row
                label={selectedZone ? "Delivery" : "Delivery"}
                value={quote ? (quote.delivery_fee === 0 ? "FREE" : `$${quote.delivery_fee.toFixed(2)}`) : "—"}
                testid="sum-delivery"
              />
              {quote && quote.fresh_surcharge_amt > 0 && (
                <div className="text-[10px] text-[#B57516] italic -mt-1">
                  Includes ${quote.fresh_surcharge_amt.toFixed(2)} fresh-delivery surcharge (Zone 2).
                </div>
              )}
              <div className="border-t border-[#1A6B2E]/10 pt-3 mt-3 flex items-baseline justify-between">
                <span className="font-label text-xs">Total (CAD)</span>
                <span data-testid="sum-total" className="font-display text-3xl">${total.toFixed(2)}</span>
              </div>
            </div>

            <button
              type="submit" data-testid="place-order-btn" disabled={!canSubmit}
              className="btn-primary w-full mt-6 rounded-full py-3.5 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Placing order…</> : <>Place order — see payment details</>}
            </button>
            <p className="text-[11px] text-[#4E5C4F] text-center mt-3">
              Payment by <b className="text-[#0E2B13]">Interac e-Transfer</b> after placing your order.
            </p>
          </div>
        </aside>
      </form>
    </main>
  );
}

function Row({ label, value, testid, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#4E5C4F]">{label}</span>
      <span data-testid={testid} className={cn("font-medium", accent && "text-[#1A6B2E]")}>{value}</span>
    </div>
  );
}
